/**
 * Modo de teste do editor de automações (equivalente ao "Execute workflow" do n8n).
 *
 * Roda o fluxo do canvas (mesmo sem salvar) uma vez e devolve o log por passo, que
 * o editor desenha nos cards (sucesso/erro) e mostra no painel de cada passo.
 * As ações do teste são REAIS (WhatsApp, negócios, webhooks de saída).
 *
 * Gatilho Webhook: "Escutar chamada de teste" abre 2 minutos em que a URL de teste
 * roda o fluxo salvo com a primeira chamada recebida; ou "Executar com a última
 * chamada" usa a chamada guardada. Demais gatilhos: JSON com o contexto do evento.
 */

import { useEffect, useRef, useState } from 'react'
import Icon from '../../components/icons.jsx'
import {
  autGet, autRun, autRuns, autTest, autTestListen, autTestListenStop, autWebhookTestUrl,
} from '../../db/api.js'
import { RUN_STATUS } from './AutomacaoExecucoes.jsx'
import { metaFor } from './nodeMeta.js'

const POLL_MS = 2000
const LISTEN_MS = 120000

function CopyUrl({ url }) {
  const [copiado, setCopiado] = useState(false)
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 1500)
    } catch { /* sem permissão de clipboard: o usuário copia na mão */ }
  }
  return (
    <div className="aut-webhook">
      <code>{url}</code>
      <button type="button" className="btn btn-outline btn-xs" onClick={copiar}>
        <Icon n={copiado ? 'check' : 'clip'} s={12} /> {copiado ? 'Copiado' : 'Copiar'}
      </button>
    </div>
  )
}

function TestResult({ run, catalog }) {
  const st = RUN_STATUS[run.status] || RUN_STATUS.success
  return (
    <div className="aut-test-result">
      <div className="aut-step-top">
        <strong>Resultado do teste</strong>
        <span className={`badge ${st.badge}`}>{st.label}</span>
      </div>
      {run.error && <p className="aut-error-text">{run.error}</p>}
      {run.status === 'waiting' && (
        <p className="field-hint">O fluxo chegou numa espera e continua sozinho depois, como uma execução normal.</p>
      )}
      <ol className="aut-steps">
        {run.steps.map((s, i) => {
          const type = catalog[s.node_type]
          return (
            <li key={`${s.node_id}-${i}`} className={`aut-step is-${s.status}`}>
              <span className={`aut-node-icon tone-${metaFor(type).tone}`}><Icon n={metaFor(type).icon} s={14} /></span>
              <div className="aut-step-body">
                <div className="aut-step-top">
                  <strong>{type?.label || s.node_type}</strong>
                  <span className={`badge ${(RUN_STATUS[s.status] || RUN_STATUS.success).badge}`}>
                    {(RUN_STATUS[s.status] || RUN_STATUS.success).label}
                  </span>
                </div>
                {s.error && <p className="aut-error-text">{s.error}</p>}
                {s.output && Object.keys(s.output).length > 0 && (
                  <details className="aut-step-data">
                    <summary>Ver dados</summary>
                    <pre>{JSON.stringify(s.output, null, 2)}</pre>
                  </details>
                )}
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

export default function AutomacaoTeste({
  automationId, triggerKey, webhookToken, sample, dirty, catalog, getDefinition,
  onResult, onSampleChanged, onClose, result,
}) {
  const isWebhook = triggerKey === 'trigger.webhook'
  const [listening, setListening] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState(null)
  const [json, setJson] = useState('{}')
  const timer = useRef(null)
  const sampleAt = useRef(null)

  const stopPolling = () => { if (timer.current) { clearInterval(timer.current); timer.current = null } }
  useEffect(() => stopPolling, [])

  const show = (e) => setError(e?.message || String(e))

  const run = async (data) => {
    setRunning(true); setError(null)
    try {
      onResult(await autTest(automationId, { definition: getDefinition(), data }))
    } catch (e) { show(e) } finally { setRunning(false) }
  }

  const runJson = () => {
    let data
    try { data = JSON.parse(json || '{}') } catch { setError('JSON inválido.'); return }
    if (!data || typeof data !== 'object' || Array.isArray(data)) { setError('Informe um objeto JSON.'); return }
    run(data)
  }

  const listen = async () => {
    setError(null)
    try {
      const atual = await autGet(automationId)
      sampleAt.current = atual.webhook_sample_at || null
      await autTestListen(automationId)
    } catch (e) { show(e); return }
    setListening(true)
    const started = Date.now()
    timer.current = setInterval(async () => {
      try {
        const row = await autGet(automationId)
        const recebeu = (row.webhook_sample_at || null) !== sampleAt.current
        if (recebeu) {
          stopPolling(); setListening(false)
          onSampleChanged(row)
          const lista = await autRuns(automationId, { limit: 5 })
          const teste = lista.find((r) => r.context?._test)
          if (teste) onResult(await autRun(teste.id))
        } else if (Date.now() - started > LISTEN_MS) {
          stopPolling(); setListening(false)
          setError('Tempo esgotado: nenhuma chamada chegou na URL de teste.')
        }
      } catch (e) { stopPolling(); setListening(false); show(e) }
    }, POLL_MS)
  }

  const cancelListen = async () => {
    stopPolling(); setListening(false)
    try { await autTestListenStop(automationId) } catch { /* a janela expira sozinha em 2 min */ }
  }

  const testUrl = autWebhookTestUrl(webhookToken)

  return (
    <div className="aut-test" role="dialog" aria-label="Testar automação">
      <header className="aut-panel-head">
        <span className="aut-node-icon tone-blue"><Icon n="zap" s={16} /></span>
        <div className="aut-panel-titles">
          <span className="aut-node-kind">Modo de teste</span>
          <h3>Testar automação</h3>
        </div>
        <button type="button" className="btn-icon" onClick={onClose} aria-label="Fechar teste">
          <Icon n="x" s={16} />
        </button>
      </header>

      <div className="aut-alert aut-alert-wait">
        <Icon n="alert" s={15} />
        <span>O teste executa as ações de verdade: envia WhatsApp, cria negócios e chama webhooks de saída.</span>
      </div>

      {isWebhook ? (
        <div className="aut-test-body">
          <p className="field-hint">
            URL de teste (vale para uma chamada, depois de clicar em Escutar; roda o fluxo <strong>salvo</strong>):
          </p>
          {testUrl ? <CopyUrl url={testUrl} /> : <p className="field-hint">Salve a automação para gerar a URL.</p>}
          {dirty && <p className="field-hint">Há alterações não salvas: a escuta usa o fluxo salvo.</p>}
          {listening ? (
            <button type="button" className="btn btn-outline btn-sm" onClick={cancelListen}>
              <Icon n="refresh" s={13} /> Escutando… (clique para cancelar)
            </button>
          ) : (
            <button type="button" className="btn btn-black btn-sm" onClick={listen} disabled={!testUrl || running}>
              Escutar chamada de teste
            </button>
          )}
          <button type="button" className="btn btn-outline btn-sm" onClick={() => run(null)}
            disabled={!sample || running || listening}
            title={sample ? undefined : 'Nenhuma chamada recebida ainda'}>
            {running ? 'Executando…' : 'Executar com a última chamada'}
          </button>
        </div>
      ) : (
        <div className="aut-test-body">
          <label className="field-label" htmlFor="aut-test-json">Dados do gatilho (JSON)</label>
          <textarea id="aut-test-json" className="input aut-textarea" rows={5} value={json}
            onChange={(e) => setJson(e.target.value)} spellCheck={false} />
          <p className="field-hint">
            Contexto do evento. Ex.: {'{"contact_id": "<id do lead>", "deal_id": "<id do negócio>", "content": "oi"}'}.
            Os passos agem sobre esse lead e negócio.
          </p>
          <button type="button" className="btn btn-black btn-sm" onClick={runJson} disabled={running}>
            {running ? 'Executando…' : 'Executar teste'}
          </button>
        </div>
      )}

      {error && <p className="aut-error-text">{error}</p>}
      {result && <TestResult run={result} catalog={catalog} />}
    </div>
  )
}
